import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, ArrowRight } from "lucide-react";
import { AVATARS } from "../lib/constants";
import { createProfile } from "../lib/storage";
import { ClayButton } from "../components/ClayButton";
import { PageShell } from "../components/PageShell";

export default function ProfileSetup({ onCreate }) {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [avatarId, setAvatarId] = useState(AVATARS[0].id);
  const [error, setError] = useState("");

  const handleStart = () => {
    if (!name.trim()) {
      setError("Escribe tu nombre para empezar");
      return;
    }
    createProfile(name, avatarId);
    if (onCreate) onCreate();
    navigate("/dashboard", { replace: true });
  };

  return (
    <PageShell>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="flex flex-col items-center justify-center gap-6 sm:gap-10 mt-6 sm:mt-10"
      >
        <motion.div
          initial={{ scale: 0.8, rotate: -10 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ type: "spring", bounce: 0.5, duration: 0.8 }}
          className="text-center"
        >
          <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur border-2 border-[#FFD700] rounded-full px-4 py-1.5 mb-4">
            <Sparkles className="w-4 h-4 text-[#FF9500]" strokeWidth={3} />
            <span className="text-sm font-bold uppercase tracking-wider text-[#7A5C00]">
              MateConMar
            </span>
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1C1C1E] mb-3">
            ¡Hola, explorador! 👋
          </h1>
          <p className="text-base sm:text-lg text-[#5C5C60] max-w-xl">
            Practica matemáticas, gana puntos y desbloquea medallas. ¿Comenzamos?
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="w-full max-w-2xl bg-white rounded-[2rem] border-4 border-white shadow-[0_20px_50px_-20px_rgba(0,0,0,0.15)] p-6 sm:p-10"
        >
          <label className="block mb-6">
            <span className="block text-sm font-bold uppercase tracking-wider text-[#5C5C60] mb-2">
              ¿Cómo te llamas?
            </span>
            <input
              type="text"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setError("");
              }}
              placeholder="Escribe tu nombre"
              maxLength={20}
              className="w-full bg-[#FFF9E6] border-4 border-[#F2ECE4] rounded-2xl px-6 py-4 text-2xl font-bold text-center focus:border-[#FF9500] focus:outline-none transition-colors placeholder:text-gray-300 placeholder:font-normal"
              data-testid="profile-name-input"
            />
            {error && (
              <p className="mt-2 text-[#FF6B6B] font-bold text-center" data-testid="profile-name-error">
                {error}
              </p>
            )}
          </label>

          <div className="mb-8">
            <p className="text-sm font-bold uppercase tracking-wider text-[#5C5C60] mb-4 text-center">
              Elige tu compañero
            </p>
            <div className="grid grid-cols-4 gap-3 sm:gap-4">
              {AVATARS.map((a) => {
                const selected = avatarId === a.id;
                return (
                  <motion.button
                    key={a.id}
                    type="button"
                    onClick={() => setAvatarId(a.id)}
                    whileTap={{ scale: 0.92 }}
                    whileHover={{ scale: 1.05 }}
                    className={`relative rounded-3xl overflow-hidden border-4 transition-all bg-[#FFF6CC] p-2 sm:p-3 ${
                      selected
                        ? "border-[#FF9500] shadow-[0_8px_20px_-5px_rgba(255,149,0,0.4)]"
                        : "border-transparent hover:border-[#FFD700]"
                    }`}
                    data-testid={`avatar-option-${a.id}`}
                  >
                    <img
                      src={a.url}
                      alt={a.name}
                      className="w-full aspect-square object-contain"
                    />
                    <span className="block mt-1 text-xs sm:text-sm font-bold text-[#1C1C1E]">
                      {a.name}
                    </span>
                    {selected && (
                      <span className="absolute top-2 right-2 w-6 h-6 rounded-full bg-[#FF9500] text-white text-xs font-bold flex items-center justify-center">
                        ✓
                      </span>
                    )}
                  </motion.button>
                );
              })}
            </div>
          </div>

          <div className="flex justify-center">
            <ClayButton
              onClick={handleStart}
              size="lg"
              data-testid="profile-start-btn"
            >
              ¡Empezar!
              <ArrowRight className="w-5 h-5" strokeWidth={3} />
            </ClayButton>
          </div>
        </motion.div>
      </motion.div>
    </PageShell>
  );
}
